// ============================================================
// TMR Portal Admin — o código coringa recém-gerado, mostrado uma única vez (31 §9)
// ============================================================
import { ModalShell } from '../../components/ModalShell';
import { CopyableText } from '../../components/CopyableText';
import { Notice } from '../../components/Notice';
import { Table, Td } from '../../components/ui';
import { Btn } from '../../components/primitives';
import type { WildcardCodeResponse } from '../../data/contingency/contract';
import { formatMoment } from '../../data/contingency/status';

/** Props for {@link WildcardCodeIssuedDialog}. */
export interface WildcardCodeIssuedDialogProps {
  /** The answer that carried the code. */
  response: WildcardCodeResponse;
  onClose: () => void;
}

/**
 * The six digits, the expiry and the sales the code opens — for the operator to check it is the right patient
 * before reading the code out.
 *
 * **This is the only time the code exists on screen.** The backend keeps none of it (the v1.1 stores only its
 * hash), so closing the dialog loses it for good, and the dialog says so. A lost code is replaced by asking for
 * another, which voids this one.
 */
export function WildcardCodeIssuedDialog({ response, onClose }: WildcardCodeIssuedDialogProps) {
  const { request, code } = response;

  return (
    <ModalShell
      title="Código coringa gerado"
      lead={`Paciente ${request.patientCpfMasked}${request.codeExpiresAt ? ` · válido até ${formatMoment(request.codeExpiresAt)}` : ''}`}
      width={600}
      onClose={onClose}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '18px 16px',
            background: 'var(--cream-100)',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          <CopyableText value={code ?? ''} style={{ fontFamily: 'var(--font-mono)', fontSize: 32, letterSpacing: '0.18em', color: 'var(--ink-900)' }}>
            {code}
          </CopyableText>
        </div>

        <Notice tone="warning">
          Este código aparece só agora: ele não fica guardado. Passe ao paciente, que digita no lugar do código de
          verificação, junto com o CPF. Se perder, gere outro — o anterior deixa de valer.
        </Notice>

        {request.sales.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span className="pa-eyebrow">Confira o paciente — o código entra nestas vendas</span>
            <Table
              dense
              columns={[{ label: 'Paciente' }, { label: 'Clínica' }, { label: 'Situação' }, { label: 'Link vale até' }]}
            >
              {request.sales.map((sale) => (
                <tr key={sale.saleId}>
                  <Td dense>{sale.patientName}</Td>
                  <Td dense>{sale.clinic}</Td>
                  <Td dense>{sale.state}</Td>
                  <Td dense>{formatMoment(sale.linkExpiresAt)}</Td>
                </tr>
              ))}
            </Table>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Btn variant="primary" onClick={onClose}>
            Já passei o código
          </Btn>
        </div>
      </div>
    </ModalShell>
  );
}
